import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Job, JobStatus } from '../types/job';
import { 
  collection, 
  addDoc, 
  query, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc, 
  DocumentData, 
  QueryDocumentSnapshot,
  QuerySnapshot,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from './AuthContext';

interface JobContextType {
  jobs: Job[];
  addJob: (job: Omit<Job, 'id' | 'updatedAt'>) => Promise<void>;
  updateJobStatus: (id: string, status: JobStatus) => Promise<void>;
  deleteJob: (id: string) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

const JobContext = createContext<JobContextType | undefined>(undefined);

export const JobProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    // First get the jobs for the current user
    const userJobsQuery = query(
      collection(db, 'jobs'),
      where('userId', '==', currentUser.uid)
    );

    // Then sort them client-side until the composite index is created
    const q = userJobsQuery;
    
    const unsubscribe = onSnapshot(q, 
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        try {
          const jobsData: Job[] = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
            const data = doc.data();
            jobsData.push({
              id: doc.id,
              userId: data.userId,
              company: data.company,
              role: data.role,
              dateApplied: data.dateApplied,
              status: data.status as JobStatus,
              updatedAt: data.updatedAt,
              notes: data.notes || ''
            });
          });
          
          // Sort jobs by updatedAt in descending order (newest first)
          const sortedJobs = [...jobsData].sort((a, b) => 
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
          
          setJobs(sortedJobs);
          setError(null);
        } catch (error) {
          console.error('Error processing jobs data:', error);
          setError(error instanceof Error ? error : new Error('Failed to process jobs data'));
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error fetching jobs:', error);
        setError(error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const addJob = useCallback(async (job: Omit<Job, 'id' | 'updatedAt' | 'userId'>) => {
    if (!currentUser) {
      throw new Error('You must be logged in to add a job');
    }

    try {
      const newJob = {
        ...job,
        id: uuidv4(),
        userId: currentUser.uid,
        updatedAt: new Date().toISOString(),
        notes: job.notes || ''
      };
      await addDoc(collection(db, 'jobs'), newJob);
    } catch (error) {
      console.error('Error adding job:', error);
      throw error;
    }
  }, [currentUser]);

  const updateJobStatus = useCallback(async (id: string, status: JobStatus) => {
    if (!currentUser) {
      throw new Error('You must be logged in to update a job');
    }

    try {
      // Verify the job belongs to the current user
      const job = jobs.find(j => j.id === id);
      if (!job || job.userId !== currentUser.uid) {
        throw new Error('Job not found or access denied');
      }

      const jobRef = doc(db, 'jobs', id);
      await updateDoc(jobRef, {
        status,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error updating job status:', error);
      throw error;
    }
  }, [currentUser, jobs]);

  const deleteJob = useCallback(async (id: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to delete a job');
    }

    try {
      // Verify the job belongs to the current user
      const job = jobs.find(j => j.id === id);
      if (!job || job.userId !== currentUser.uid) {
        throw new Error('Job not found or access denied');
      }

      await deleteDoc(doc(db, 'jobs', id));
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  }, [currentUser, jobs]);

  return (
    <JobContext.Provider value={{ 
      jobs, 
      addJob, 
      updateJobStatus, 
      deleteJob, 
      loading, 
      error 
    }}>
      {children}
    </JobContext.Provider>
  );
};

export const useJobs = () => {
  const context = useContext(JobContext);
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};
